import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// ============================================================================
// Frontend-facing types (what components consume)
// ============================================================================

export interface Plan {
  id: string;
  name: string;
  description: string;
  priceUsd: number;
  features: string[];
  highlighted?: boolean;
}

export interface PaymentToken {
  ticker: string;
  name: string;
  precision: number;
}

export interface ApiConfig {
  plans: Plan[];
  paymentTokens: PaymentToken[];
  receiverWallet: string;
  discounts: Record<number, number>;
  isLoading: boolean;
  error: string | null;
  apiUrl: string;
}

// ============================================================================
// API-shape types (Session 1, PT-P1-5 pragmatic fix)
//
// These mirror dexscan-api's serde structs so the response-path bugs
// (PT-P1-1 / PT-P1-2) can't recur silently. Optional fields use `?` because
// the Rust side returns Option<T>. If the API ever drops or renames a
// field, TypeScript compile fails instead of silently producing undefined.
// ============================================================================

interface ApiPlanFeaturesObject {
  endpoints?: 'all' | string[];
  websocket?: boolean;
  priority_support?: boolean;
  custom_endpoints?: boolean;
}

interface ApiPlanRow {
  slug: string;
  name: string;
  description?: string;
  price_usd: number | string;
  rate_limit_per_min?: number;
  daily_request_limit?: number;
  monthly_request_limit?: number;
  features?: ApiPlanFeaturesObject | string[];
  /** PT-P2-12: admin-configurable "Most Popular" badge.
   *  NOTE 2026-05-25: the API doesn't surface this field YET — the
   *  `SubscriptionPlan` Rust struct + the SELECT in `get_subscription_plans`
   *  need an `is_highlighted` column on `api_plans` first. Until that
   *  ships, `transformPlanRow` falls back to `slug === 'pro'` (existing
   *  behaviour). Half-landed by design — the portal-side is forward-
   *  compatible. Track as a future API-side mini-bundle. */
  is_highlighted?: boolean;
}

interface ApiPaymentTokenRow {
  ticker: string;
  name: string;
  precision?: number;
}

interface ApiDiscountRow {
  months?: number;
  discount_percent?: number;
}

interface ApiPlansEnvelope {
  code: string;
  data?: {
    plans?: ApiPlanRow[];
    accepted_tokens?: ApiPaymentTokenRow[];
    discounts?: ApiDiscountRow[];
    receiver_wallet?: string;
  };
}

// ============================================================================
// Initial state — empty + loading.
//
// Session 1 (PT-P1-3): the prior "fall back to hardcoded defaults on error"
// block in fetchConfig was DELETED. It hardcoded stale plans + a stale
// receiver wallet (`klv1v8ken…`) that hasn't matched production for some
// time. If the API fetch failed, the portal would render plausible-looking
// plans + a wrong receiver wallet, and users would sign payments to an
// unmonitored address — silent money loss. The new contract: on fetch
// failure, isLoading=false + error set + empty data. Components MUST check
// `config.error` before rendering checkout UI.
// ============================================================================

const defaultConfig: ApiConfig = {
  plans: [],
  paymentTokens: [],
  receiverWallet: '',
  discounts: {},
  isLoading: true,
  error: null,
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8090',
};

const ConfigContext = createContext<ApiConfig>(defaultConfig);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<ApiConfig>(defaultConfig);

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8090';

    try {
      const response = await fetch(`${apiUrl}/subscription/plans`);
      if (!response.ok) {
        throw new Error(`Failed to fetch subscription plans (HTTP ${response.status})`);
      }

      // Cast the parsed JSON to our typed envelope. Runtime shape is still
      // trusted from the API; TypeScript catches code-side drift only.
      const data = (await response.json()) as ApiPlansEnvelope;

      if (data.code !== 'ok' || !data.data) {
        throw new Error('Invalid API response shape');
      }

      // Session 1 (PT-P1-3): receiver wallet is REQUIRED. The previous
      // `|| ''` silently produced an empty string that downstream sign
      // sites would happily use. Now: if absent, the entire config load
      // is treated as a failure (fall through to the catch).
      const receiverWallet = data.data.receiver_wallet;
      if (!receiverWallet || receiverWallet.length === 0) {
        throw new Error('API returned no receiver_wallet');
      }

      // Session 1 (PT-P1-4): optional cross-check against an env-var
      // anchor. If `VITE_RECEIVER_WALLET` is set at build time AND the
      // API returns a different value, fail-closed — refuse to load
      // checkout UI. This prevents a compromised or misconfigured API
      // from silently redirecting payments to an attacker's wallet
      // without an attacker also compromising the build pipeline.
      const expectedReceiver = import.meta.env.VITE_RECEIVER_WALLET as string | undefined;
      if (expectedReceiver && expectedReceiver !== receiverWallet) {
        throw new Error(
          `Receiver wallet mismatch: build anchor expected ${expectedReceiver}, API returned ${receiverWallet}`
        );
      }

      // Transform API response to frontend format
      const plans: Plan[] = (data.data.plans || []).map(transformPlanRow);
      const paymentTokens: PaymentToken[] = (data.data.accepted_tokens || []).map(
        transformPaymentTokenRow
      );

      // Convert discounts array to Record<months, decimal>
      const discounts: Record<number, number> = {};
      (data.data.discounts || []).forEach((d) => {
        if (d.months !== undefined && d.discount_percent !== undefined) {
          discounts[d.months] = d.discount_percent / 100;
        }
      });

      setConfig({
        plans,
        paymentTokens,
        receiverWallet,
        discounts,
        isLoading: false,
        error: null,
        apiUrl,
      });
    } catch (err) {
      console.error('Failed to load config:', err);
      // Fail-closed: empty config + error string. Components that gate on
      // `config.error` render an error UI; checkout flows must NOT render
      // when receiverWallet is the empty string (defense in depth).
      setConfig({
        plans: [],
        paymentTokens: [],
        receiverWallet: '',
        discounts: {},
        isLoading: false,
        error:
          err instanceof Error
            ? err.message
            : 'Failed to load configuration. Please retry.',
        apiUrl,
      });
    }
  };

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

// ============================================================================
// Typed mappers (PT-P1-5 pragmatic types)
// ============================================================================

function transformPlanRow(p: ApiPlanRow): Plan {
  const featuresList: string[] = [];

  // Rate limit
  if (p.rate_limit_per_min) {
    featuresList.push(`${p.rate_limit_per_min} requests/minute`);
  }

  // Daily / monthly limits
  if (p.daily_request_limit) {
    featuresList.push(`${p.daily_request_limit.toLocaleString()} requests/day`);
  } else if (p.monthly_request_limit) {
    featuresList.push(`${p.monthly_request_limit.toLocaleString()} requests/month`);
  } else {
    featuresList.push('Unlimited requests');
  }

  // Features object OR array
  if (p.features) {
    if (Array.isArray(p.features)) {
      featuresList.push(...p.features);
    } else {
      const f = p.features;
      if (f.endpoints === 'all') {
        featuresList.push('All API endpoints');
      } else if (Array.isArray(f.endpoints)) {
        featuresList.push(`Endpoints: ${f.endpoints.join(', ')}`);
      }
      if (f.websocket) featuresList.push('WebSocket access');
      if (f.priority_support) featuresList.push('Priority support');
      if (f.custom_endpoints) featuresList.push('Custom endpoints');
    }
  }

  // PT-P2-12: prefer admin-set `is_highlighted` if API surfaces it; fall
  // back to the legacy `slug === 'pro'` rule for back-compat.
  const highlighted =
    typeof p.is_highlighted === 'boolean' ? p.is_highlighted : p.slug === 'pro';

  return {
    id: p.slug,
    name: p.name,
    description: p.description || '',
    priceUsd: typeof p.price_usd === 'number' ? p.price_usd : parseFloat(p.price_usd) || 0,
    features: featuresList,
    highlighted,
  };
}

function transformPaymentTokenRow(t: ApiPaymentTokenRow): PaymentToken {
  return {
    ticker: t.ticker,
    name: t.name,
    precision: t.precision || 6,
  };
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
}
